import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useForm, type FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, ArrowUpRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { BOOKING_URL, BUSINESS, formatISODate } from "@/lib/site";
import { trackEvent } from "@/lib/analytics";
import {
  contactFormSchema,
  INQUIRY_TYPES,
  type ContactApiResponse,
  type ContactFormValues,
} from "@/lib/contact-schema";
import { Reveal } from "../Reveal";

type Status = "idle" | "submitting" | "success" | "error";

export function ContactForm() {
  const statusRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const startedAt = useMemo(() => Date.now(), []);
  const [status, setStatus] = useState<Status>("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const formStartedTracked = useRef(false);
  const formId = useId();

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      preferredResponse: "Email",
      inquiryType: "" as ContactFormValues["inquiryType"],
      arrivalDate: "",
      departureDate: "",
      subject: "",
      message: "",
      website: "",
      formStartedAt: startedAt,
      referringPage: "",
      timezone: "",
    },
    mode: "onSubmit",
  });

  const preferredResponse = form.watch("preferredResponse");
  const messageValue = form.watch("message") ?? "";
  const today = useMemo(() => formatISODate(new Date()), []);
  const errorEntries = Object.entries(form.formState.errors);

  useEffect(() => {
    if (typeof window === "undefined") return;
    form.setValue("referringPage", window.location.href);
    form.setValue("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone ?? "");
  }, [form]);

  useEffect(() => {
    if (status === "success" || status === "error") {
      statusRef.current?.focus();
    }
  }, [status]);

  function markStarted() {
    if (formStartedTracked.current) return;
    formStartedTracked.current = true;
    trackEvent("contact_form_started");
  }

  async function onSubmit(values: ContactFormValues) {
    setStatus("submitting");
    setStatusMessage("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          formStartedAt: startedAt,
        }),
      });

      const data = (await res.json()) as ContactApiResponse;

      if (!res.ok || !data.ok) {
        if (data.ok === false && data.fieldErrors) {
          for (const [field, messages] of Object.entries(data.fieldErrors)) {
            if (field === "_form") continue;
            form.setError(field as keyof ContactFormValues, {
              type: "server",
              message: messages[0] ?? "Invalid value.",
            });
          }
          summaryRef.current?.focus();
        }
        setStatus("error");
        setStatusMessage(
          data.ok === false ? data.message : "Please try again or contact Amigo Inn directly.",
        );
        trackEvent("contact_form_error");
        return;
      }

      setStatus("success");
      setStatusMessage(data.message);
      form.reset({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        preferredResponse: "Email",
        inquiryType: "" as ContactFormValues["inquiryType"],
        arrivalDate: "",
        departureDate: "",
        subject: "",
        message: "",
        website: "",
        formStartedAt: Date.now(),
        referringPage: typeof window !== "undefined" ? window.location.href : "",
        timezone:
          typeof window !== "undefined"
            ? (Intl.DateTimeFormat().resolvedOptions().timeZone ?? "")
            : "",
      });
      trackEvent("contact_form_submitted");
    } catch {
      setStatus("error");
      setStatusMessage(
        `Please try again or contact Amigo Inn directly at ${BUSINESS.phone} or ${BUSINESS.email}.`,
      );
      trackEvent("contact_form_error");
    }
  }

  function onInvalid(errors: FieldErrors<ContactFormValues>) {
    const firstError = Object.keys(errors)[0];
    requestAnimationFrame(() => {
      if (firstError) {
        const byId = document.getElementById(`${formId}-${firstError}`);
        const byName = document.querySelector<HTMLElement>(`[name="${firstError}"]`);
        (byId ?? byName)?.focus();
        return;
      }
      summaryRef.current?.focus();
    });
  }

  if (status === "success") {
    return (
      <div
        ref={statusRef}
        tabIndex={-1}
        role="status"
        aria-live="polite"
        className="rounded-sm border border-forest/20 bg-surface p-6 sm:p-8"
      >
        <p className="eyebrow text-clay">Message Sent</p>
        <h3 className="mt-3 font-display text-2xl text-forest-deep sm:text-3xl">
          Thank You for Contacting Amigo Inn
        </h3>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">{statusMessage}</p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Button asChild variant="book" size="cta">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("booking_link_clicked", { location: "contact_success" })}
            >
              Check Availability <ArrowUpRight aria-hidden="true" />
            </a>
          </Button>
          <Button asChild variant="outlineForest" size="cta">
            <Link to="/">Return to Homepage</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Reveal x={28}>
      <div className="relative rounded-sm border border-sand/70 bg-surface p-5 shadow-[var(--shadow-soft)] sm:p-8">
        <p className="eyebrow text-clay">Send Us a Message</p>
        <h2
          id="contact-form-title"
          className="mt-3 font-display text-3xl leading-tight text-forest-deep sm:text-[2.4rem]"
        >
          How Can We Help?
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Complete the form below and tell us what you need. A member of the Amigo Inn team will
          respond as soon as possible during regular office hours.
        </p>

        <p className="mt-5 rounded-sm border border-sand bg-secondary/70 px-4 py-3 text-sm text-forest-deep">
          <strong className="font-semibold">Prefer a Phone Call?</strong> Select &quot;Phone&quot;
          as your preferred response method and include the best telephone number for reaching you.
        </p>

        {errorEntries.length > 0 && (
          <div
            ref={summaryRef}
            tabIndex={-1}
            role="alert"
            className="mt-6 rounded-sm border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            <p className="font-semibold">Please correct the following:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {errorEntries.map(([field, error]) => (
                <li key={field}>
                  <a
                    href={`#${formId}-${field}`}
                    className="underline underline-offset-2"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(`${formId}-${field}`)?.focus();
                    }}
                  >
                    {error?.message || field}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {status === "error" && (
          <div
            ref={statusRef}
            tabIndex={-1}
            role="alert"
            aria-live="assertive"
            className="mt-6 rounded-sm border border-destructive/30 bg-destructive/5 px-4 py-4"
          >
            <p className="font-display text-xl text-forest-deep">
              We Couldn&apos;t Send Your Message
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{statusMessage}</p>
            <p className="mt-3 text-sm text-forest-deep">
              Call{" "}
              <a href={BUSINESS.phoneHref} className="link-underline font-semibold">
                {BUSINESS.phone}
              </a>{" "}
              or email{" "}
              <a href={`mailto:${BUSINESS.email}`} className="link-underline font-semibold">
                {BUSINESS.email}
              </a>
              .
            </p>
          </div>
        )}

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, onInvalid)}
            onFocusCapture={markStarted}
            className="mt-8 space-y-6"
            noValidate
            aria-labelledby="contact-form-title"
          >
            {/* Honeypot */}
            <div
              className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden"
              aria-hidden="true"
            >
              <Label htmlFor={`${formId}-website`}>Website</Label>
              <Input
                id={`${formId}-website`}
                tabIndex={-1}
                autoComplete="off"
                {...form.register("website")}
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor={`${formId}-firstName`}>First Name *</FormLabel>
                    <FormControl>
                      <Input
                        id={`${formId}-firstName`}
                        autoComplete="given-name"
                        className="h-12 rounded-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor={`${formId}-lastName`}>Last Name *</FormLabel>
                    <FormControl>
                      <Input
                        id={`${formId}-lastName`}
                        autoComplete="family-name"
                        className="h-12 rounded-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor={`${formId}-email`}>Email Address *</FormLabel>
                  <FormControl>
                    <Input
                      id={`${formId}-email`}
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      className="h-12 rounded-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="preferredResponse"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preferred Response Method *</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="grid gap-3 sm:grid-cols-2"
                    >
                      {(["Email", "Phone"] as const).map((option) => (
                        <Label
                          key={option}
                          className="flex min-h-12 cursor-pointer items-center gap-3 rounded-sm border border-input px-4 py-3 text-sm font-medium"
                        >
                          <RadioGroupItem value={option} id={`${formId}-pref-${option}`} />
                          {option}
                        </Label>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor={`${formId}-phone`}>
                    Phone Number{preferredResponse === "Phone" ? " *" : ""}
                  </FormLabel>
                  <FormControl>
                    <Input
                      id={`${formId}-phone`}
                      type="tel"
                      autoComplete="tel"
                      inputMode="tel"
                      className="h-12 rounded-sm"
                      {...field}
                    />
                  </FormControl>
                  {preferredResponse === "Phone" && (
                    <FormDescription>Required because you prefer a phone response.</FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="inquiryType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Inquiry Type *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || undefined}>
                    <FormControl>
                      <SelectTrigger
                        id={`${formId}-inquiryType`}
                        name="inquiryType"
                        className="h-12 rounded-sm"
                        aria-required="true"
                      >
                        <SelectValue placeholder="Select a topic" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {INQUIRY_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-5 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="arrivalDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor={`${formId}-arrivalDate`}>Arrival Date</FormLabel>
                    <FormControl>
                      <Input
                        id={`${formId}-arrivalDate`}
                        type="date"
                        min={today}
                        className="h-12 rounded-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="departureDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor={`${formId}-departureDate`}>Departure Date</FormLabel>
                    <FormControl>
                      <Input
                        id={`${formId}-departureDate`}
                        type="date"
                        min={form.watch("arrivalDate") || today}
                        className="h-12 rounded-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor={`${formId}-subject`}>Subject</FormLabel>
                  <FormControl>
                    <Input id={`${formId}-subject`} className="h-12 rounded-sm" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor={`${formId}-message`}>Comment or Message *</FormLabel>
                  <FormControl>
                    <Textarea
                      id={`${formId}-message`}
                      rows={6}
                      maxLength={2000}
                      className="min-h-36 rounded-sm"
                      {...field}
                    />
                  </FormControl>
                  <div className="flex items-center justify-between gap-3">
                    <FormMessage />
                    <p className="ml-auto text-xs text-muted-foreground" aria-live="polite">
                      {messageValue.length}/2000
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <Button
              type="submit"
              variant="book"
              size="ctaLg"
              className="w-full sm:w-auto"
              disabled={status === "submitting"}
            >
              {status === "submitting" ? (
                <>
                  <Loader2 className="animate-spin" aria-hidden="true" /> Sending Message…
                </>
              ) : (
                <>
                  Send My Message <ArrowRight aria-hidden="true" />
                </>
              )}
            </Button>
          </form>
        </Form>
      </div>
    </Reveal>
  );
}
